/**
 * Stock Leakage Prevention — Type Model
 * ─────────────────────────────────────────────────────────────────────
 * Sibling layer to WarehouseInventoryContext (canonical on-hand source).
 * Adds the four discipline layers a CFO/COO requires:
 *
 *   1. Reservation — locks stock to a job at staging trigger (~7 days
 *      pre-install). Prevents two jobs from claiming the same boxes.
 *   2. Field Purchase Log — captures retail buys with a reason code.
 *      Soft gate: log + reason required, but never blocks the work.
 *   3. Cycle Counts — ABC tiered cadence (A weekly, B monthly, C quarterly).
 *      Variance vs system creates a Variance Event (proof-based).
 *   4. Variance Events — the audit trail. Every count, pull, and return
 *      that diverges from the system creates one.
 *
 * Per memory:
 *   • Warehouse/Field roles see quantities only — no $.
 *   • CFO/Bookkeeper/Owner see dollar shrinkage on the Leakage Report.
 *   • Proof-based: every variance/field-purchase requires reason + actor.
 */

export type AbcClass = "A" | "B" | "C";

/** Reason codes for variance events. Drives the leakage scorecard. */
export type VarianceReason =
  | "cycle_count"        // routine count caught a discrepancy
  | "field_pull"         // installer grabbed without job assignment
  | "return_to_stock"    // leftover material returned post-install
  | "damaged"            // damaged in warehouse / handling
  | "miscount_at_receive" // PO said 50, only 48 arrived (caught later)
  | "lost"               // unexplained — the worst code, triggers review
  | "transfer_out"       // moved to another warehouse
  | "adjustment";        // bookkeeper adjustment with note

/** Reason codes a field installer picks when buying retail. */
export type FieldPurchaseReason =
  | "stock_out"          // we should have had it, we didn't
  | "damaged_on_site"    // had it, broke it
  | "design_change"      // customer changed scope
  | "miscounted_in_kit"  // staging missed an item
  | "specialty_item"     // not stocked, intentional retail buy
  | "other";

/** Lifecycle of a job-bound reservation. */
export type ReservationStatus =
  | "reserved"     // staged hold, available drops, on-hand unchanged
  | "picked"       // warehouse pulled from shelf — on-hand decrements
  | "released"     // reservation lifted (job canceled/delayed/rescoped)
  | "shorted";     // stock ran out before pick — escalated

/** A job's reservation against a stock SKU. */
export interface StockReservation {
  id: string;
  jobId: string;
  jobLabel: string;
  sku: string;
  productName: string;
  quantity: number;
  status: ReservationStatus;
  reservedAt: string;
  reservedBy: string;
  /** Reason captured on release/short. */
  releaseReason?: string;
  pickedAt?: string;
  pickedBy?: string;
  /** When set, this reservation auto-expires (e.g. job rescheduled out). */
  expiresAt?: string;
}

/** A retail purchase made by an installer / field crew. */
export interface FieldPurchase {
  id: string;
  jobId: string;
  jobLabel: string;
  purchasedBy: string;
  purchasedAt: string;
  vendor: string;             // "Home Depot", "Lowe's", "Local supply"
  amount: number;             // total $ (operators see only the qty/SKUs; CFO sees $)
  /** Items bought. SKU optional — retail SKUs rarely match our catalog. */
  items: Array<{
    description: string;
    quantity: number;
    catalogSku?: string;      // matched canonical SKU when known
  }>;
  reason: FieldPurchaseReason;
  notes?: string;
  receiptPhotoUrl?: string;   // proof-of-execution
  /** Bookkeeper review queue. */
  reviewStatus: "pending" | "approved" | "flagged";
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNote?: string;
}

/** A cycle count event — recorded shelf count vs system count. */
export interface CycleCount {
  id: string;
  sku: string;
  productName: string;
  abcClass: AbcClass;
  systemQuantity: number;     // what the system said before the count
  countedQuantity: number;    // what the counter physically saw
  variance: number;           // counted - system (negative = leakage)
  countedBy: string;
  countedAt: string;
  /** Captured if variance ≠ 0. Required by UI for any non-zero count. */
  varianceReason?: VarianceReason;
  varianceNote?: string;
  /** Auto-created variance event linked back. */
  varianceEventId?: string;
}

/** Audit-grade record of any inventory delta outside a normal pick. */
export interface VarianceEvent {
  id: string;
  sku: string;
  productName: string;
  quantityDelta: number;       // negative = stock went down vs expected
  reason: VarianceReason;
  reasonNote?: string;
  /** Optional links — present when the event came from a specific source. */
  jobId?: string;
  jobLabel?: string;
  cycleCountId?: string;
  fieldPurchaseId?: string;
  reservationId?: string;
  /** Estimated $ impact — calculated from SKU unit cost. CFO view only. */
  costImpact?: number;
  recordedBy: string;
  recordedAt: string;
}

/** Per-SKU policy: ABC class, min/max, unit cost (for $ leakage calc). */
export interface StockPolicy {
  sku: string;
  abcClass: AbcClass;
  /** Reorder point — when available drops below this, suggest a stock PO. */
  minQuantity: number;
  /** Order-up-to target. */
  maxQuantity: number;
  /** Unit cost in $ — drives leakage scorecard. */
  unitCost: number;
  /** Last full count timestamp (drives "due for count" logic). */
  lastCountedAt?: string;
}

/** Computed cadence flag for the UI. */
export interface CountDueRow {
  sku: string;
  productName: string;
  abcClass: AbcClass;
  daysSinceLastCount: number | null;  // null = never counted
  daysUntilDue: number;               // negative = overdue
  isOverdue: boolean;
}

/** Reservation cadence per memory choice. */
export const RESERVATION_TRIGGER_DAYS_BEFORE_INSTALL = 7;

/** ABC count cadence (industry standard). */
export const COUNT_CADENCE_DAYS: Record<AbcClass, number> = {
  A: 7,    // weekly — top movers, highest leakage exposure
  B: 30,   // monthly
  C: 90,   // quarterly
};
